import { useState, useEffect } from "react";

const gold = "#A0845C";
const navy = "#0F1B2D";
const navyLight = "#1A2E47";

const AREA_COLORS = ["#1A56DB","#057A55","#E02424","#D97706","#7E3AF2","#0694A2","#E74694"];
const AREAS = ["Faith","Fitness","Family","Finance","Fun","Firm","Friends"];
const AREA_ICONS = { Faith:"✝️", Fitness:"💪", Family:"🏠", Finance:"💰", Fun:"🎉", Firm:"💼", Friends:"🤝" };
const AREA_SUBTITLES = { Faith:"Spiritual growth", Fitness:"Physical health", Family:"Home & loved ones", Finance:"Stewardship", Fun:"Hobbies & rest", Firm:"Career & calling", Friends:"Community" };

const MODULES = [
  { icon:"🎁", title:"Spiritual Gifts", desc:"Discover your God-given gifts across Romans 12, 1 Cor 12 & Eph 4.", color:"#1A56DB" },
  { icon:"🧠", title:"DISC Profile", desc:"Understand how you lead, relate, and work with others.", color:"#7E3AF2" },
  { icon:"💰", title:"Personal Budget", desc:"Monthly tracking with annual rollup — stewardship made simple.", color:"#057A55" },
  { icon:"📖", title:"Bible Reading Plan", desc:"Choose a plan and track daily reading with streak counting.", color:"#D97706" },
  { icon:"💪", title:"Fitness Tracker", desc:"Weekly habit tracker for exercise, nutrition, sleep and hydration.", color:"#E02424" },
];

const FEATURES = [
  { icon:"◎", title:"Plan", desc:"Set your life vision and goals across all 7 areas with biblical intentionality." },
  { icon:"◷", title:"Act", desc:"Schedule your priorities with a weekly time map before the urgent crowds out the important." },
  { icon:"◈", title:"Review", desc:"Score your life wheel and reflect on what God is doing in and through you." },
  { icon:"◉", title:"Adjust", desc:"Stop, start, continue — and surrender what only God can change." },
  { icon:"◭", title:"Deep Dive", desc:"Go deeper in each area with handles, self-ratings, goals, and guided assessments." },
  { icon:"◫", title:"Modules", desc:"Focused tools — spiritual gifts, DISC, budget, Bible plan, fitness tracking and more." },
];

const PROBLEMS = [
  { icon:"😔", title:"You're busy but not fruitful", desc:"You work hard but feel like you're spinning wheels — no clear direction, no sense of progress." },
  { icon:"🔀", title:"Life feels fragmented", desc:"Work, faith, family, health — each pulling in different directions with no unifying framework." },
  { icon:"📋", title:"Goals don't stick", desc:"You set goals in January and forget them by March. Without a system, intentions stay intentions." },
];

const TESTIMONIALS = [
  { name:"Priya S.", role:"Business owner, Bangalore", text:"This framework changed how I approach every week. The Deep Dive on Faith alone was worth it — I finally have clarity on my spiritual gifts and how to use them.", initial:"P", color:"#1A56DB" },
  { name:"Rahul M.", role:"Pastor, Hyderabad", text:"I've tried dozens of productivity tools but nothing brought together my faith, family, and work like this. The Plan-Act-Review-Adjust cycle is genius.", initial:"R", color:"#057A55" },
  { name:"Sarah K.", role:"Teacher, Chennai", text:"The Bible reading plan with streak tracking keeps me accountable like nothing else has. Simple, beautiful, and deeply meaningful.", initial:"S", color:"#E02424" },
];

function FloatingOrbs() {
  return (
    <div style={{ position:"absolute", inset:0, overflow:"hidden", pointerEvents:"none" }}>
      {AREA_COLORS.map((color, i) => (
        <div key={i} style={{
          position:"absolute",
          width: 200 + (i * 40),
          height: 200 + (i * 40),
          borderRadius:"50%",
          background: `radial-gradient(circle, ${color}25 0%, transparent 70%)`,
          left: `${(i * 15) % 80}%`,
          top: `${(i * 20) % 70}%`,
          animation: `float${i} ${6 + i}s ease-in-out infinite`,
          transform: "translate(-50%, -50%)",
        }} />
      ))}
      <style>{`
        ${AREA_COLORS.map((_, i) => `
          @keyframes float${i} {
            0%, 100% { transform: translate(-50%, -50%) scale(1); }
            50% { transform: translate(-50%, -50%) scale(1.1) translate(${i % 2 === 0 ? 20 : -20}px, ${i % 3 === 0 ? 15 : -15}px); }
          }
        `).join("")}
      `}</style>
    </div>
  );
}

function NavBar({ onLogin, onSignup }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <nav style={{
      position:"fixed", top:0, left:0, right:0, zIndex:100,
      background: scrolled ? "rgba(15,27,45,0.95)" : "transparent",
      backdropFilter: scrolled ? "blur(10px)" : "none",
      borderBottom: scrolled ? "0.5px solid rgba(255,255,255,0.1)" : "none",
      transition:"all 0.3s", padding:"16px 24px",
      display:"flex", justifyContent:"space-between", alignItems:"center",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <div style={{ width:32, height:32, borderRadius:8, background:gold, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>✦</div>
        <span style={{ color:"#fff", fontWeight:500, fontSize:16 }}>My SMF</span>
      </div>
      <div style={{ display:"flex", gap:10 }}>
        <button onClick={onLogin} style={{ padding:"8px 18px", borderRadius:8, border:"0.5px solid rgba(255,255,255,0.3)", background:"transparent", color:"#fff", fontSize:13, cursor:"pointer" }}>Sign in</button>
        <button onClick={onSignup} style={{ padding:"8px 18px", borderRadius:8, border:"none", background:gold, color:"#fff", fontSize:13, fontWeight:500, cursor:"pointer" }}>Get started free</button>
      </div>
    </nav>
  );
}

export default function LandingPage({ onLogin, onSignup, onTryDemo, onPrivacy }) {
  const scrollTo = id => document.getElementById(id)?.scrollIntoView({ behavior:"smooth" });

  return (
    <div style={{ fontFamily:"system-ui,sans-serif", background:"#fff", color:"#1a1a1a", overflowX:"hidden" }}>
      <NavBar onLogin={onLogin} onSignup={onSignup} />

      {/* Hero */}
      <section style={{ background:navy, minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", position:"relative", padding:"120px 24px 80px" }}>
        <FloatingOrbs />
        <div style={{ maxWidth:700, textAlign:"center", position:"relative", zIndex:1 }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"rgba(160,132,92,0.15)", border:"0.5px solid rgba(160,132,92,0.4)", borderRadius:20, padding:"6px 16px", marginBottom:24 }}>
            <span style={{ fontSize:12, color:gold }}>✦</span>
            <span style={{ fontSize:12, color:gold, fontWeight:500 }}>"For I know the plans I have for you..." — Jeremiah 29:11</span>
          </div>
          <h1 style={{ fontSize:"clamp(36px,6vw,64px)", fontWeight:700, color:"#fff", lineHeight:1.15, margin:"0 0 20px", letterSpacing:"-0.02em" }}>
            Manage Your Life<br />
            <span style={{ background:`linear-gradient(135deg, ${gold}, #E8C57A)`, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>
              From a Biblical Perspective
            </span>
          </h1>
          <p style={{ fontSize:"clamp(16px,2vw,20px)", color:"rgba(255,255,255,0.7)", lineHeight:1.7, margin:"0 0 40px", maxWidth:560, marginLeft:"auto", marginRight:"auto" }}>
            A complete self-management framework covering all 7 areas of life — Faith, Fitness, Family, Finance, Fun, Firm, and Friends — with tools, assessments, and guided modules.
          </p>
          <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
            <button onClick={onSignup} style={{ padding:"14px 32px", borderRadius:10, border:"none", background:gold, color:"#fff", fontSize:16, fontWeight:600, cursor:"pointer", boxShadow:"0 4px 24px rgba(160,132,92,0.4)" }}>
              Get Started Free →
            </button>
            <button onClick={onTryDemo} style={{ padding:"14px 32px", borderRadius:10, border:"0.5px solid rgba(255,255,255,0.3)", background:"transparent", color:"#fff", fontSize:16, cursor:"pointer" }}>
              Try Demo
            </button>
          </div>
          <p style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginTop:16 }}>No credit card required · Free to use</p>
        </div>
        <div style={{ position:"absolute", bottom:32, left:"50%", transform:"translateX(-50%)", animation:"bounce 2s infinite" }}>
          <div style={{ width:24, height:40, border:"2px solid rgba(255,255,255,0.3)", borderRadius:12, display:"flex", alignItems:"flex-start", justifyContent:"center", paddingTop:6 }}>
            <div style={{ width:4, height:8, background:"rgba(255,255,255,0.5)", borderRadius:2, animation:"scroll 2s infinite" }} />
          </div>
        </div>
        <style>{`
          @keyframes bounce { 0%,100%{transform:translateX(-50%) translateY(0)} 50%{transform:translateX(-50%) translateY(8px)} }
          @keyframes scroll { 0%{opacity:1;transform:translateY(0)} 100%{opacity:0;transform:translateY(12px)} }
        `}</style>
      </section>

      {/* Problem */}
      <section style={{ padding:"80px 24px", background:"#f8f6f2" }}>
        <div style={{ maxWidth:960, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <p style={{ fontSize:13, color:gold, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.1em", margin:"0 0 12px" }}>The Challenge</p>
            <h2 style={{ fontSize:"clamp(28px,4vw,42px)", fontWeight:700, color:navy, margin:"0 0 16px", letterSpacing:"-0.02em" }}>Most people never live intentionally</h2>
            <p style={{ fontSize:16, color:"#666", maxWidth:500, margin:"0 auto" }}>Life happens to them instead of through them. Sound familiar?</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:20 }}>
            {PROBLEMS.map((p,i) => (
              <div key={i} style={{ background:"#fff", borderRadius:16, padding:"28px 24px", border:"0.5px solid rgba(0,0,0,0.08)", boxShadow:"0 2px 12px rgba(0,0,0,0.04)" }}>
                <div style={{ fontSize:32, marginBottom:16 }}>{p.icon}</div>
                <h3 style={{ fontSize:18, fontWeight:600, color:navy, margin:"0 0 10px" }}>{p.title}</h3>
                <p style={{ fontSize:14, color:"#666", lineHeight:1.7, margin:0 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Framework */}
      <section style={{ padding:"80px 24px", background:navy }}>
        <div style={{ maxWidth:960, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <p style={{ fontSize:13, color:gold, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.1em", margin:"0 0 12px" }}>The Solution</p>
            <h2 style={{ fontSize:"clamp(28px,4vw,42px)", fontWeight:700, color:"#fff", margin:"0 0 16px", letterSpacing:"-0.02em" }}>A proven 4-phase cycle</h2>
            <p style={{ fontSize:16, color:"rgba(255,255,255,0.6)", maxWidth:500, margin:"0 auto" }}>Built on biblical wisdom and practical discipline.</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:16 }}>
            {[
              { phase:"01", label:"Plan", ref:"Proverbs 16:3", desc:"Define your vision and set goals across all life areas.", color:"#1A56DB" },
              { phase:"02", label:"Act", ref:"Ephesians 5:16", desc:"Schedule priorities before the urgent crowds out the important.", color:"#057A55" },
              { phase:"03", label:"Review", ref:"Lamentations 3:40", desc:"Examine each area with honesty, grace, and truth.", color:"#D97706" },
              { phase:"04", label:"Adjust", ref:"Proverbs 15:22", desc:"Apply what review reveals and act before the next cycle.", color:"#E02424" },
            ].map((p,i) => (
              <div key={i} style={{ background:"rgba(255,255,255,0.05)", border:`0.5px solid ${p.color}40`, borderRadius:16, padding:"24px 20px", position:"relative", overflow:"hidden" }}>
                <div style={{ fontSize:48, fontWeight:800, color:`${p.color}20`, position:"absolute", top:-8, right:12, lineHeight:1 }}>{p.phase}</div>
                <div style={{ width:36, height:36, borderRadius:8, background:`${p.color}20`, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:14 }}>
                  <div style={{ width:12, height:12, borderRadius:"50%", background:p.color }} />
                </div>
                <h3 style={{ fontSize:20, fontWeight:700, color:"#fff", margin:"0 0 6px" }}>{p.label}</h3>
                <p style={{ fontSize:11, color:p.color, margin:"0 0 10px", fontStyle:"italic" }}>{p.ref}</p>
                <p style={{ fontSize:13, color:"rgba(255,255,255,0.6)", lineHeight:1.6, margin:0 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 Life Areas */}
      <section style={{ padding:"80px 24px", background:"#fff" }}>
        <div style={{ maxWidth:960, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <p style={{ fontSize:13, color:gold, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.1em", margin:"0 0 12px" }}>The 7 F's</p>
            <h2 style={{ fontSize:"clamp(28px,4vw,42px)", fontWeight:700, color:navy, margin:"0 0 16px", letterSpacing:"-0.02em" }}>Every area of your life, covered</h2>
            <p style={{ fontSize:16, color:"#666", maxWidth:500, margin:"0 auto" }}>No area left behind. No area neglected. A truly whole-life approach.</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:12 }}>
            {AREAS.map((area,i) => (
              <div key={i} style={{ background:AREA_COLORS[i]+"10", border:`0.5px solid ${AREA_COLORS[i]}30`, borderRadius:16, padding:"20px 14px", textAlign:"center" }}>
                <div style={{ fontSize:28, marginBottom:10 }}>{AREA_ICONS[area]}</div>
                <div style={{ fontSize:15, fontWeight:700, color:AREA_COLORS[i], marginBottom:4 }}>{area}</div>
                <div style={{ fontSize:11, color:"#666" }}>{AREA_SUBTITLES[area]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding:"80px 24px", background:"#f8f6f2" }}>
        <div style={{ maxWidth:960, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <p style={{ fontSize:13, color:gold, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.1em", margin:"0 0 12px" }}>Features</p>
            <h2 style={{ fontSize:"clamp(28px,4vw,42px)", fontWeight:700, color:navy, margin:"0 0 16px", letterSpacing:"-0.02em" }}>Everything you need to thrive</h2>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:16 }}>
            {FEATURES.map((f,i) => (
              <div key={i} style={{ background:"#fff", borderRadius:16, padding:"24px 20px", border:"0.5px solid rgba(0,0,0,0.08)", display:"flex", gap:16, alignItems:"flex-start" }}>
                <div style={{ width:40, height:40, borderRadius:10, background:`${gold}15`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0, color:gold }}>{f.icon}</div>
                <div>
                  <h3 style={{ fontSize:16, fontWeight:600, color:navy, margin:"0 0 6px" }}>{f.title}</h3>
                  <p style={{ fontSize:13, color:"#666", lineHeight:1.6, margin:0 }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules */}
      <section style={{ padding:"80px 24px", background:navy }}>
        <div style={{ maxWidth:960, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <p style={{ fontSize:13, color:gold, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.1em", margin:"0 0 12px" }}>Modules</p>
            <h2 style={{ fontSize:"clamp(28px,4vw,42px)", fontWeight:700, color:"#fff", margin:"0 0 16px", letterSpacing:"-0.02em" }}>Focused tools for deeper growth</h2>
            <p style={{ fontSize:16, color:"rgba(255,255,255,0.6)", maxWidth:500, margin:"0 auto" }}>Each module is a standalone tool you can use independently or as part of your framework.</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))", gap:16 }}>
            {MODULES.map((m,i) => (
              <div key={i} style={{ background:"rgba(255,255,255,0.05)", border:`0.5px solid ${m.color}40`, borderRadius:16, padding:"24px 20px" }}>
                <div style={{ width:48, height:48, borderRadius:12, background:`${m.color}20`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, marginBottom:14 }}>{m.icon}</div>
                <h3 style={{ fontSize:16, fontWeight:600, color:"#fff", margin:"0 0 8px" }}>{m.title}</h3>
                <p style={{ fontSize:13, color:"rgba(255,255,255,0.6)", lineHeight:1.6, margin:0 }}>{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Try Demo */}
      <section style={{ padding:"80px 24px", background:`linear-gradient(135deg, ${gold}15, #fff)` }}>
        <div style={{ maxWidth:700, margin:"0 auto", textAlign:"center" }}>
          <div style={{ width:64, height:64, borderRadius:16, background:gold, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, margin:"0 auto 24px" }}>✦</div>
          <h2 style={{ fontSize:"clamp(24px,4vw,38px)", fontWeight:700, color:navy, margin:"0 0 16px", letterSpacing:"-0.02em" }}>Try it before you sign up</h2>
          <p style={{ fontSize:16, color:"#555", lineHeight:1.7, margin:"0 0 32px" }}>
            Explore the full framework in demo mode. Fill in your vision, set goals, score your life wheel — and when you're ready to save your progress, create a free account in seconds.
          </p>
          <button onClick={onTryDemo} style={{ padding:"14px 36px", borderRadius:10, border:"none", background:navy, color:"#fff", fontSize:16, fontWeight:600, cursor:"pointer" }}>
            Try the Demo →
          </button>
        </div>
      </section>

      {/* Scripture */}
      <section style={{ padding:"80px 24px", background:navy, textAlign:"center" }}>
        <div style={{ maxWidth:600, margin:"0 auto" }}>
          <div style={{ fontSize:48, color:gold, marginBottom:16, lineHeight:1 }}>"</div>
          <p style={{ fontSize:"clamp(18px,3vw,26px)", color:"#fff", lineHeight:1.7, fontStyle:"italic", margin:"0 0 20px", fontWeight:300 }}>
            For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future.
          </p>
          <p style={{ fontSize:14, color:gold, fontWeight:600, letterSpacing:"0.05em" }}>JEREMIAH 29:11</p>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding:"80px 24px", background:"#f8f6f2" }}>
        <div style={{ maxWidth:960, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <p style={{ fontSize:13, color:gold, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.1em", margin:"0 0 12px" }}>Testimonials</p>
            <h2 style={{ fontSize:"clamp(28px,4vw,42px)", fontWeight:700, color:navy, margin:0, letterSpacing:"-0.02em" }}>Changing lives, one area at a time</h2>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:20 }}>
            {TESTIMONIALS.map((t,i) => (
              <div key={i} style={{ background:"#fff", borderRadius:16, padding:"28px 24px", border:"0.5px solid rgba(0,0,0,0.08)", boxShadow:"0 2px 12px rgba(0,0,0,0.04)" }}>
                <div style={{ display:"flex", gap:4, marginBottom:16 }}>
                  {[...Array(5)].map((_,j) => <span key={j} style={{ color:gold, fontSize:14 }}>★</span>)}
                </div>
                <p style={{ fontSize:14, color:"#444", lineHeight:1.8, margin:"0 0 20px", fontStyle:"italic" }}>"{t.text}"</p>
                <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                  <div style={{ width:40, height:40, borderRadius:"50%", background:t.color, display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontWeight:700, fontSize:16 }}>{t.initial}</div>
                  <div>
                    <p style={{ fontSize:14, fontWeight:600, color:navy, margin:0 }}>{t.name}</p>
                    <p style={{ fontSize:12, color:"#888", margin:0 }}>{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ padding:"100px 24px", background:`linear-gradient(135deg, ${navy}, #1A2E47)`, textAlign:"center", position:"relative", overflow:"hidden" }}>
        <FloatingOrbs />
        <div style={{ position:"relative", zIndex:1, maxWidth:600, margin:"0 auto" }}>
          <h2 style={{ fontSize:"clamp(28px,5vw,48px)", fontWeight:700, color:"#fff", margin:"0 0 16px", letterSpacing:"-0.02em" }}>
            Start living intentionally today
          </h2>
          <p style={{ fontSize:16, color:"rgba(255,255,255,0.7)", margin:"0 0 36px", lineHeight:1.7 }}>
            Join others who are managing their lives from a biblical perspective. Free to use, no credit card required.
          </p>
          <button onClick={onSignup} style={{ padding:"16px 40px", borderRadius:12, border:"none", background:gold, color:"#fff", fontSize:18, fontWeight:700, cursor:"pointer", boxShadow:"0 4px 32px rgba(160,132,92,0.5)" }}>
            Create Your Free Account →
          </button>
          <p style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginTop:16 }}>Free forever · No credit card needed</p>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background:"#0A1520", padding:"40px 24px", textAlign:"center" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, marginBottom:12 }}>
          <div style={{ width:28, height:28, borderRadius:6, background:gold, display:"flex", alignItems:"center", justifyContent:"center", fontSize:14 }}>✦</div>
          <span style={{ color:"#fff", fontWeight:500, fontSize:15 }}>My Self-Management Framework</span>
        </div>
        <p style={{ fontSize:13, color:"rgba(255,255,255,0.4)", margin:"0 0 16px" }}>"For I know the plans I have for you..." — Jeremiah 29:11</p>
        <div style={{ display:"flex", gap:20, justifyContent:"center", flexWrap:"wrap" }}>
          <button onClick={onLogin} style={{ background:"transparent", border:"none", color:"rgba(255,255,255,0.5)", fontSize:13, cursor:"pointer" }}>Sign in</button>
          <button onClick={onSignup} style={{ background:"transparent", border:"none", color:"rgba(255,255,255,0.5)", fontSize:13, cursor:"pointer" }}>Get started</button>
          <button onClick={onTryDemo} style={{ background:"transparent", border:"none", color:"rgba(255,255,255,0.5)", fontSize:13, cursor:"pointer" }}>Try demo</button>
          <button onClick={onPrivacy} style={{ background:"transparent", border:"none", color:"rgba(255,255,255,0.5)", fontSize:13, cursor:"pointer" }}>Privacy Policy</button>
        </div>
        <p style={{ fontSize:11, color:"rgba(255,255,255,0.2)", marginTop:24 }}>© {new Date().getFullYear()} My Self-Management Framework. Built with purpose.</p>
      </footer>
    </div>
  );
}
